import React, { ReactNode } from "react";
import { i18n } from "i18n";

import {
  BreakdownClosedEarlyLabel,
  IInboxRowLabelProps,
} from "component-library";

import {
  formatDateTime,
  prepareClosingDate2,
} from "shared/helpers/breakdownViewHelpers";
import { findFacetName } from "shared/helpers/facetHelpers";
import { LinkmailType } from "shared/models/breakdown";
import { FacetCategory, IFacetGroup } from "shared/models/facet";

import PageKey from "config/PageKey";

import { IListItem } from "models/common";
import { IJobItem, JobItemType } from "models/job";
import { ICommonTranslation, IJobsPageTranslation } from "models/translation";

import defaultsWithFallback from "helpers/defaultsWithFallback";
import { ListItemTranslator } from "helpers/translationHelpers";
import { fallbackTranslation } from "services/api/fallbackData";
import applicationMap from "services/applicationMap";

import {
  FilterType,
  IJobSender,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { JobItemToJobTableItemConverterTexts } from "pages/JobsPage/shared/shared.converters";

import { IJobTableItemTag } from "components/JobsTable/JobsTable.models";

export const closingDateKeys = {
  today: "today",
  tomorrow: "tomorrow",
  other: "other",
  closed: "closed",
};

export interface IPrepareJobTitleData {
  type: JobItemType;
  title: string;
  roleTitle?: string;
  productionTypeId: number;
  linkmailType?: LinkmailType;
  amendmentCount?: number;
}

export interface IPrepareJobTitleTexts {
  linkmailTypeAmendment: string;
  updateLabel: string;
  roleTitleFormat: string;
}

export interface IPrepareJobLabelTexts {
  suggestedLabel: string;
  nudgedLabel: string;
  updateLabel: string;
}

export interface IPrepareJobSenderTexts {
  senderNameAnonymous: string;
}

export interface IPrepareClosingDateText {
  closingDateLabels: IListItem[];
  dateTimeFormat: string;
}

export interface IPrepareAdditionalInfoTexts extends IPrepareClosingDateText {
  sentOnFormat: string;
}

export interface IPrepareJobTagsData {
  type: JobItemType;
  productionTypeId: number;
  contractTypeId: number;
  productionLocationId?: string;
  productionLocation?: string;
  isLocalHire?: boolean;
}

export interface IPrepareAdditionalInfoData {
  type?: JobItemType;
  sentDate: Date | string;
  closingDate?: Date | string;
  freezeDate?: Date | string;
  closedEarly?: boolean;
  closedReason?: string;
}

function getSentOnText(
  sentOnFormat: string,
  dateTimeFormat: string,
  date: string | Date
): string {
  return sentOnFormat.replace("{date}", formatDateTime(dateTimeFormat, date));
}

function prepareClosingDateText(
  texts: IPrepareClosingDateText,
  item: IPrepareAdditionalInfoData
): ReactNode {
  if (item?.closedEarly) {
    return (
      <BreakdownClosedEarlyLabel
        closeDate={item.closingDate as string}
        reason={item.closedReason}
        className="text-red-cardinal"
      />
    );
  }

  const listItemTranslator = new ListItemTranslator(
    texts.closingDateLabels,
    fallbackTranslation.common.breakdowns.closingDateLabels
  );

  if (item.type === JobItemType.FollowUp || item.closingDate == null) {
    return "";
  }

  const closingDateTexts = {
    closed: listItemTranslator.get(closingDateKeys.closed),
    today: listItemTranslator.get(closingDateKeys.today),
    tomorrow: listItemTranslator.get(closingDateKeys.tomorrow),
    other: listItemTranslator.get(closingDateKeys.other),
    dateTimeFormat: texts.dateTimeFormat,
  };

  return prepareClosingDate2(
    closingDateTexts,
    item.closingDate,
    item.freezeDate
  );
}

export function prepareAdditionalInfo(
  item: IPrepareAdditionalInfoData,
  texts: IPrepareAdditionalInfoTexts
) {
  const sentOn = getSentOnText(
    texts.sentOnFormat,
    texts.dateTimeFormat,
    item.sentDate
  );
  const closesOn = prepareClosingDateText(texts, item);

  return [sentOn, closesOn];
}

export function buildJobTitle(
  title: string,
  productionTypeName: string | undefined,
  linkmailTypeName: string | undefined,
  additionalDetails?: string
) {
  const titleParts = [
    title,
    productionTypeName && `(${productionTypeName})`,
    linkmailTypeName && `(${linkmailTypeName})`,
    additionalDetails && `(${additionalDetails})`,
  ];

  return titleParts.filter(Boolean).join(" ");
}

export const prepareJobTitle = (
  jobData: IPrepareJobTitleData,
  texts: IPrepareJobTitleTexts,
  productionTypes: IFacetGroup[]
) => {
  const productionTypePart = productionTypes
    ? findFacetName(productionTypes, jobData.productionTypeId)
    : undefined;

  const linkmailTypePart =
    jobData.linkmailType === LinkmailType.Amendment &&
    jobData.amendmentCount != null
      ? texts.linkmailTypeAmendment.replace(
          "{number}",
          jobData.amendmentCount.toString()
        )
      : undefined;

  switch (jobData.type) {
    case JobItemType.Linkmail:
      return buildJobTitle(jobData.title, productionTypePart, linkmailTypePart);
    case JobItemType.Role:
      return texts.roleTitleFormat
        .replace("{role}", jobData.roleTitle!)
        .replace(
          "{breakdown}",
          buildJobTitle(jobData.title, productionTypePart, linkmailTypePart)
        );
    case JobItemType.FollowUp:
      return buildJobTitle(
        jobData.title,
        productionTypePart,
        linkmailTypePart,
        texts.updateLabel
      );
  }
};

export const prepareJobSender = (
  senderId: string,
  sender: string,
  texts: IPrepareJobSenderTexts
): IJobSender => {
  return {
    id: sender ? senderId : undefined,
    name: sender || texts.senderNameAnonymous,
  };
};

export const prepareJobUuid = (item: IJobItem): string => {
  return `${item.type}_${item.linkmailId}_${
    item.roleId || item.followUpId || ""
  }`;
};

export function prepareJobLabel(
  item: IJobItem,
  texts: IPrepareJobLabelTexts,
  showRoleCastLabel: boolean
): IInboxRowLabelProps[] | undefined {
  const labels = [];

  if (item.isSuggested) {
    labels.push({ text: texts.suggestedLabel, className: "bg-purple-rich" });
  }

  if (item.isNudged) {
    labels.push({ text: texts.nudgedLabel, className: "bg-green-river" });
  }

  if (item.type === JobItemType.FollowUp) {
    labels.push({
      text: texts.updateLabel,
      className: "bg-color-neutral-one",
    });
  }

  if (showRoleCastLabel) {
    labels.push({
      text: i18n.t("common:jobsFeed.inboxRow.labels.roleCast"),
      className: "bg-orange-burnt",
    });
  }

  return labels;
}

export const prepareJobTags = (
  jobData: IPrepareJobTagsData,
  productionTypes: IFacetGroup[],
  contractTypes: IFacetGroup[]
): IJobTableItemTag[] => {
  const {
    type,
    productionTypeId,
    contractTypeId,
    productionLocationId,
    isLocalHire,
    productionLocation,
  } = jobData;

  if (type === JobItemType.FollowUp) {
    return [];
  }

  const commonTags = [
    {
      filterType: FilterType.ProductionType,
      facetCategory: FacetCategory.ProductionType,
      facetId: productionTypeId,
      name: findFacetName(productionTypes, productionTypeId),
    },
    {
      filterType: FilterType.ContractType,
      facetCategory: FacetCategory.ContractType,
      facetId: contractTypeId,
      name: findFacetName(contractTypes, contractTypeId),
    },
  ].filter((x) => Boolean(x.name));

  if (isLocalHire && productionLocationId) {
    const name = productionLocation ?? "";

    return [
      {
        filterType: FilterType.Locations,
        facetCategory: FacetCategory.Location,
        facetId: [
          {
            value: productionLocationId,
            name,
          },
        ],
        name,
      },
      ...commonTags,
    ];
  }

  return commonTags;
};

export const prepareJobUrl = ({
  type,
  linkmailId,
  roleId,
  followUpId,
}: IJobItem) => {
  switch (type) {
    case JobItemType.Linkmail:
      return applicationMap.links.jobBreakdown(linkmailId);
    case JobItemType.FollowUp:
      return applicationMap.links.jobFollowUp(linkmailId, followUpId!);
    case JobItemType.Role:
      return applicationMap.links.jobRole(linkmailId, roleId!);
    default:
      return undefined;
  }
};

export function prepareJobItemToJobTableItemConverterTexts(
  pageTranslation: IJobsPageTranslation,
  commonTranslation: ICommonTranslation
): JobItemToJobTableItemConverterTexts {
  const pageShared = defaultsWithFallback(
    pageTranslation?.shared,
    fallbackTranslation[PageKey.Jobs].shared
  );
  const breakdowns = defaultsWithFallback(
    commonTranslation?.breakdowns,
    fallbackTranslation.common.breakdowns
  );
  const followups = defaultsWithFallback(
    commonTranslation?.followups,
    fallbackTranslation.common.followups
  );
  const grid = defaultsWithFallback(
    commonTranslation?.grid,
    fallbackTranslation.common.grid
  );

  return {
    senderNameAnonymous: grid.senderNameAnonymous,
    closingDateLabels: breakdowns.closingDateLabels,
    sentOnFormat: breakdowns.sentOnFormat,
    dateTimeFormat: breakdowns.dateTimeFormat,
    linkmailTypeAmendment: breakdowns.linkmailTypeAmendment,
    updateLabel: followups.updateLabel,
    roleTitleFormat: pageShared?.roleTitleFormat,
    suggestedLabel: pageShared.suggestedLabel,
    nudgedLabel: pageShared.nudgedLabel,
  };
}
